/**
 * Copyright Highway9 Networks Inc.
 *
 * Client for the AI assistant chat surface exposed by saas-api at
 * /api/v1/saas/ai-assistant (REST proxy) and /api/v1/saas/ai-assistant/ws
 * (websocket bridge that streams upstream SSE events to the browser).
 *
 * Domain types live in @h9/ui-lib (AiAssistantAdapter contract). This module
 * owns CSP auth, REST paths, and the browser WebSocket bridge only.
 *
 * Authentication: REST uses the Bearer token like every other saas-ui call;
 * the websocket appends the JWT as `?token=` because browsers cannot set
 * custom headers on WS handshakes.
 */

import {
  formatAssistantErrorMessage,
  normalizeAiAssistantMessage,
  normalizeAiAssistantSession,
  type AiAssistantAgent,
  type AiAssistantMessage,
  type AiAssistantMessagePart,
  type AiAssistantObjectRef,
  type AiAssistantQuestionRequest,
  type AiAssistantSession,
  type AiAssistantSessionScope,
  type AiAssistantSocketHandlers,
  type ClientEnvelope,
} from "@h9/ui-lib/components/organisms/AiAssistant";
import { authenticationService } from "./authentication-service";
import type { User } from "~/types/user";

const REST_BASE = "/api/v1/saas/ai-assistant";

function sessionScopeQuery(scope: AiAssistantSessionScope): string {
  return `scope=${encodeURIComponent(scope)}`;
}

// Re-export contract types + shared helpers so existing saas-ui callers keep
// importing from this service without depending on the ui-lib path.
export { formatAssistantErrorMessage } from "@h9/ui-lib/components/organisms/AiAssistant";
export type {
  AiAssistantAgent,
  AiAssistantMessage,
  AiAssistantMessageInfo,
  AiAssistantMessagePart,
  AiAssistantObjectRef,
  AiAssistantQuestionInfo,
  AiAssistantQuestionOption,
  AiAssistantQuestionRequest,
  AiAssistantSession,
  AiAssistantSessionScope,
  AiAssistantSocketHandlers,
  ClientEnvelope,
} from "@h9/ui-lib/components/organisms/AiAssistant";

export {
  isDefaultAssistantSessionTitle,
  normalizeAiAssistantSession,
  DEFAULT_PRIMARY_AGENT_CANONICAL,
  isAiAssistantAgentHiddenFromUi,
  filterAgentsVisibleForUi,
  DEFAULT_AGENT_DISPLAY_NAMES,
  agentDisplayLabel,
  pickAgentDisplayNamesFromAssistantConfig,
  isAiAssistantModelIdDenylisted,
  stripAssistantH9ModelDisplayPrefix,
  findModelValueInProvidersConfig,
  collectModelIdsFromProvidersConfig,
  collectProviderModelCanonicalEntries,
  buildAssistantModelIdToLabelMap,
  normalizeOutgoingAssistantModel,
  assistantModelIdFallbackLabel,
  assistantModelMenuLabel,
  pickTopLevelModelFromAssistantConfig,
  pickGlobalDefaultModelId,
  resolveDefaultEffectiveModelId,
} from "@h9/ui-lib/components/organisms/AiAssistant";

/** Logged-in user as an assistant object reference (for message attribution in the UI). */
export function currentAssistantUserRef(): AiAssistantObjectRef | undefined {
  const info = authenticationService.currentUserInfo as User | null;
  if (info?.id) {
    const name = info.name?.trim() || info.email?.trim();
    return name ? { id: info.id, name } : { id: info.id };
  }
  const legacy = authenticationService.currentUserValue as {
    id?: string;
    email?: string;
    name?: string;
  } | null;
  if (legacy?.id) {
    const name = legacy.name?.trim() || legacy.email?.trim();
    return name ? { id: legacy.id, name } : { id: legacy.id };
  }
  return undefined;
}

const authHeader = () => ({
  Authorization: `Bearer ${authenticationService?.currentUserValue?.token ?? ""}`,
});

const safeJson = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

/**
 * Server config is fixed for the life of a session and several panels read it, so the
 * in-flight promise is reused rather than refetched per panel and per tab activation.
 */
const configPromiseCache = new Map<string, Promise<unknown>>();
const cachedConfigGet = (key: string, load: () => Promise<unknown>): Promise<unknown> => {
  const hit = configPromiseCache.get(key);
  if (hit) return hit;
  const pending = load().catch((err) => {
    configPromiseCache.delete(key);
    throw err;
  });
  configPromiseCache.set(key, pending);
  return pending;
};

const ensureOk = async (response: Response, op: string) => {
  if (!response.ok) {
    const body = await safeJson(response);
    const msg =
      (body && typeof body === "object" && "errors" in body
        ? // saas-style { errors: [{ message }] }
          (body as any).errors?.[0]?.message
        : undefined) ||
      (typeof body === "string" ? body : undefined) ||
      `${response.status} ${response.statusText}`;
    throw new Error(`AI assistant ${op} failed: ${msg}`);
  }
};

export const aiAssistantRest = {
  async health(): Promise<{ healthy: boolean; version?: string }> {
    const r = await fetch(`${REST_BASE}/health`, { headers: { ...authHeader() } });
    await ensureOk(r, "health");
    return (await safeJson(r)) as any;
  },

  async listAgents(): Promise<AiAssistantAgent[]> {
    const r = await fetch(`${REST_BASE}/agents`, { headers: { ...authHeader() } });
    await ensureOk(r, "list agents");
    const body = (await safeJson(r)) as { data?: AiAssistantAgent[] } | null;
    return body?.data ?? [];
  },

  /** Upstream `GET /config` — includes top-level `model` (server default). */
  fetchConfig(): Promise<unknown> {
    return cachedConfigGet("config", async () => {
      const r = await fetch(`${REST_BASE}/config`, { headers: { ...authHeader() } });
      await ensureOk(r, "get assistant config");
      return await safeJson(r);
    });
  },

  /** Upstream `GET /config/providers` — shape varies slightly by upstream version. */
  listConfigProviders(): Promise<unknown> {
    return cachedConfigGet("config/providers", async () => {
      const r = await fetch(`${REST_BASE}/config/providers`, { headers: { ...authHeader() } });
      await ensureOk(r, "list config providers");
      return await safeJson(r);
    });
  },

  async listSessions(scope: AiAssistantSessionScope = "team"): Promise<AiAssistantSession[]> {
    const r = await fetch(`${REST_BASE}/sessions?${sessionScopeQuery(scope)}`, {
      headers: { ...authHeader() },
    });
    await ensureOk(r, "list sessions");
    const body = (await safeJson(r)) as { data?: AiAssistantSession[] } | null;
    return (body?.data ?? [])
      .map((s) => normalizeAiAssistantSession(s))
      .filter((s): s is AiAssistantSession => Boolean(s?.id));
  },

  async getSession(id: string): Promise<AiAssistantSession> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}`, {
      headers: { ...authHeader() },
    });
    await ensureOk(r, "get session");
    const body = await safeJson(r);
    let raw: unknown = body;
    if (body && typeof body === "object" && "data" in (body as object) && (body as any).data) {
      raw = (body as { data: unknown }).data;
    }
    return normalizeAiAssistantSession(raw, id) ?? { id };
  },

  /** Load a session-scoped Highcharts JSON config saved by chart-tool. */
  async fetchSessionChart(sessionId: string, filename: string): Promise<Record<string, unknown>> {
    const safeName = filename.trim();
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(sessionId)}/charts/${encodeURIComponent(safeName)}`,
      { headers: { ...authHeader() } }
    );
    await ensureOk(r, "load chart");
    const body = await safeJson(r);
    if (!body || typeof body !== "object" || Array.isArray(body)) {
      throw new Error("Invalid chart configuration");
    }
    return body as Record<string, unknown>;
  },

  /** Load a session-scoped HTML report saved by report-tool. */
  async fetchSessionReport(sessionId: string, filename: string): Promise<string> {
    const safeName = filename.trim();
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(sessionId)}/reports/${encodeURIComponent(safeName)}`,
      { headers: { ...authHeader() } }
    );
    await ensureOk(r, "load report");
    return r.text();
  },

  async createSession(title?: string): Promise<AiAssistantSession> {
    const r = await fetch(`${REST_BASE}/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(title ? { title } : {}),
    });
    await ensureOk(r, "create session");
    const body = await safeJson(r);
    return normalizeAiAssistantSession(body) ?? { id: "" };
  },

  async deleteSession(id: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...authHeader() },
    });
    await ensureOk(r, "delete session");
  },

  /** Updates display title in Postgres only (not sent to the upstream assistant). */
  async patchSessionTitle(id: string, title: string): Promise<AiAssistantSession> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ title }),
    });
    await ensureOk(r, "update session title");
    const body = await safeJson(r);
    const existing = normalizeAiAssistantSession(body, id);
    return existing ?? { id, title };
  },

  async listMessages(
    id: string,
    limit = 50,
    scope: AiAssistantSessionScope = "team"
  ): Promise<AiAssistantMessage[]> {
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(id)}/messages?limit=${limit}&${sessionScopeQuery(scope)}`,
      { headers: { ...authHeader() } }
    );
    await ensureOk(r, "list messages");
    const body = (await safeJson(r)) as { data?: AiAssistantMessage[] } | null;
    return (body?.data ?? []).map((m) => normalizeAiAssistantMessage(m));
  },

  async sendMessage(
    id: string,
    body: {
      prompt?: string;
      parts?: AiAssistantMessagePart[];
      agent?: string;
      model?: string;
      timezone?: string;
    }
  ): Promise<AiAssistantMessage> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(body),
    });
    await ensureOk(r, "send message");
    return (await safeJson(r)) as AiAssistantMessage;
  },

  async sendMessageAsync(
    id: string,
    body: {
      prompt?: string;
      parts?: AiAssistantMessagePart[];
      agent?: string;
      model?: string;
    }
  ): Promise<void> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}/messages/async`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(body),
    });
    await ensureOk(r, "send message async");
  },

  async abort(id: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}/abort`, {
      method: "POST",
      headers: { ...authHeader() },
    });
    await ensureOk(r, "abort");
  },

  async listPendingQuestions(): Promise<AiAssistantQuestionRequest[]> {
    const r = await fetch(`${REST_BASE}/questions`, { headers: { ...authHeader() } });
    await ensureOk(r, "list pending questions");
    const body = (await safeJson(r)) as
      | { data?: AiAssistantQuestionRequest[] }
      | AiAssistantQuestionRequest[]
      | null;
    if (Array.isArray(body)) return body;
    return body?.data ?? [];
  },

  async replyToQuestion(requestID: string, answers: string[][]): Promise<void> {
    const r = await fetch(`${REST_BASE}/questions/${encodeURIComponent(requestID)}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ answers }),
    });
    await ensureOk(r, "reply to question");
  },

  async rejectQuestion(requestID: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/questions/${encodeURIComponent(requestID)}/reject`, {
      method: "POST",
      headers: { ...authHeader() },
    });
    await ensureOk(r, "reject question");
  },
};

// ── WebSocket bridge ───────────────────────────────────────────────────────
// Wire format for frames from the Go bridge. Not part of the ui-lib contract;
// only this client parses it.

export type ServerEnvelope =
  | { type: "server.connected"; email: string }
  | { type: "server.event"; sessionID: string; event: any }
  | { type: "server.message_complete"; sessionID: string; message: AiAssistantMessage }
  | { type: "server.session_created"; session: AiAssistantSession }
  | { type: "server.session_deleted"; sessionID: string }
  | { type: "server.aborted"; sessionID: string }
  | { type: "server.error"; message: string }
  | { type: "server.pong"; ts: number };

export class AiAssistantSocket {
  private ws?: WebSocket;
  private retry = 0;
  private closedByClient = false;
  private subscriptions: Set<string> = new Set();
  private pingTimer?: ReturnType<typeof setInterval>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;

  constructor(private handlers: AiAssistantSocketHandlers) {}

  connect() {
    this.closedByClient = false;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const token = authenticationService?.currentUserValue?.token ?? "";
    const url = `${proto}://${window.location.host}/api/v1/saas/ai-assistant/ws?token=${encodeURIComponent(token)}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.retry = 0;
      // Re-subscribe after a reconnect
      if (this.subscriptions.size > 0) {
        this.send({ type: "client.subscribe", data: { sessionIDs: [...this.subscriptions] } });
      }
      this.pingTimer = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.send({ type: "client.ping" });
        }
      }, 25_000);
    };

    this.ws.onmessage = (ev) => {
      let msg: ServerEnvelope;
      try {
        msg = JSON.parse(ev.data) as ServerEnvelope;
      } catch {
        return;
      }
      this.dispatch(msg);
    };

    this.ws.onclose = () => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.handlers.onClose?.();
      if (!this.closedByClient) {
        const wait = Math.min(15_000, 1000 * 2 ** this.retry);
        this.retry += 1;
        this.reconnectTimer = setTimeout(() => this.connect(), wait);
      }
    };

    this.ws.onerror = () => {
      // onclose will follow
    };
  }

  close() {
    this.closedByClient = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    // Cancel any pending reconnect so close() is final (MCS-4316)
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    const ws = this.ws;
    this.ws = undefined;
    if (ws) {
      // Detach before closing. Frames already buffered still fire onmessage during the close
      // handshake, and onopen can still land if we were mid-connect, installing a ping interval
      // that nothing clears. onclose stays bound so the consumer still sees the disconnect.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.close();
    }
  }

  send(envelope: ClientEnvelope) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(envelope));
      return true;
    }
    return false;
  }

  subscribe(sessionIDs: string[]) {
    this.subscriptions = new Set(sessionIDs);
    this.send({ type: "client.subscribe", data: { sessionIDs } });
  }

  addSubscription(sessionID: string) {
    if (!sessionID || this.subscriptions.has(sessionID)) return;
    this.subscriptions.add(sessionID);
    this.send({
      type: "client.subscribe",
      data: { sessionIDs: [...this.subscriptions] },
    });
  }

  private dispatch(raw: unknown) {
    if (!raw || typeof raw !== "object") return;
    const m = raw as Record<string, unknown>;
    const type = String(m.type ?? "").toLowerCase();

    switch (type) {
      case "server.connected":
        this.handlers.onConnected?.(String(m.email ?? ""));
        break;

      case "server.event": {
        const sid = String(m.sessionID ?? m.sessionId ?? "");
        const ev = m.event !== undefined ? m.event : m;
        this.handlers.onEvent?.(sid, ev);
        break;
      }

      case "server.message_complete": {
        const sessionID = String(m.sessionID ?? m.sessionId ?? "");
        let message = m.message as any;
        if (message && typeof message === "object" && !message.info) {
          if (message.message?.info) {
            message = message.message;
          }
        }
        if (sessionID && message && typeof message === "object" && message.info?.id) {
          this.handlers.onMessageComplete?.(sessionID, message as AiAssistantMessage);
        }
        break;
      }

      case "server.session_created":
        this.handlers.onSessionCreated?.(m.session as AiAssistantSession);
        break;

      case "server.session_deleted":
        this.handlers.onSessionDeleted?.(String(m.sessionID ?? m.sessionId ?? ""));
        break;

      case "server.aborted":
        this.handlers.onAborted?.(String(m.sessionID ?? m.sessionId ?? ""));
        break;

      case "server.error":
        this.handlers.onError?.(
          formatAssistantErrorMessage(String(m.message ?? (m as any).error ?? "unknown error"))
        );
        break;

      case "server.pong":
        break;

      default:
        break;
    }
  }
}
